import { jsx as _jsx } from "react/jsx-runtime";
// Assistant-bubble markdown renderer (fn-49.2, R2/R3).
//
// Renders agent reply text as GFM markdown via react-markdown. Safety
// posture (documented in the package README):
//
//   - react-markdown builds REAL React elements — there is no
//     `dangerouslySetInnerHTML` anywhere, and raw HTML in the input is
//     escaped/ignored by default (we never add `rehype-raw`).
//   - `defaultUrlTransform` is passed EXPLICITLY and must never be
//     replaced with an identity transform (that exact mistake is
//     GHSA-fpw4-p57j-hqmq). It empties every href/src whose protocol
//     is not in `https?|ircs?|mailto|xmpp` — `javascript:`,
//     `vbscript:`, `file:` and ALL `data:` URIs come out as `""`.
//   - The `components.a` override adds `rel="noopener noreferrer"` +
//     `target="_blank"`. `urlTransform` runs BEFORE the components map
//     (verified in react-markdown lib/index.js), so the override only
//     ever receives already-sanitized hrefs.
//   - Images are NOT rendered. `defaultUrlTransform` still allows
//     http(s) srcs, and an `<img>` from untrusted assistant text would
//     fire a remote fetch on render (tracking/referrer leak). The
//     `components.img` override degrades image syntax to its alt text;
//     no network request ever leaves the bubble.
//   - Oversize messages (> MAX_MARKDOWN_CHARS) skip the parse entirely
//     and render as plain text — react-markdown has no upstream DoS
//     guard.
//   - A per-bubble error boundary catches any parse/render throw and
//     falls back to the same plain-text rendering; one broken message
//     never takes down the chat.
//
// Memoization: MA delivers whole messages (no token streaming), so a
// single `React.memo` per bubble suffices. The components map and the
// plugins array live at module scope — fresh per-render refs would
// defeat react-markdown's internal memoization.
import { Component, memo } from 'react';
import ReactMarkdown, { defaultUrlTransform } from 'react-markdown';
import remarkGfm from 'remark-gfm';
/**
 * Messages longer than this render as plain text without parsing
 * (~50KB — far beyond any legitimate chat reply).
 */
export const MAX_MARKDOWN_CHARS = 50_000;
/** Plain-text fallback (oversize / parse failure). `pre-wrap` via CSS. */
function PlainText(props) {
    return _jsx("p", { className: "sbui-md-plain", children: props.content });
}
// Module-level STABLE refs — see header comment.
const COMPONENTS = {
    a: ({ node: _node, children, ...rest }) => (
    // Sanitized href (urlTransform already ran). rel+target: correct
    // escape from a future iframe embed (ADR 0027 v2a).
    _jsx("a", { ...rest, rel: "noopener noreferrer", target: "_blank", children: children })),
    // No images: an http(s) <img> src would fetch a remote resource the
    // moment the bubble renders — a tracking pixel from untrusted
    // assistant text. Degrade to the alt text (or nothing).
    img: ({ alt }) => typeof alt === 'string' && alt.length > 0 ? (_jsx("span", { className: "sbui-md-img-alt", children: alt })) : null,
};
const REMARK_PLUGINS = [remarkGfm];
/** Per-bubble error boundary: any markdown render throw → plain text. */
class MarkdownErrorBoundary extends Component {
    state = { failed: false };
    static getDerivedStateFromError() {
        return { failed: true };
    }
    render() {
        if (this.state.failed)
            return _jsx(PlainText, { content: this.props.content });
        return this.props.children;
    }
}
function MarkdownContentImpl(props) {
    const content = props.content;
    if (content.length > MAX_MARKDOWN_CHARS) {
        return _jsx(PlainText, { content: content });
    }
    return (_jsx(MarkdownErrorBoundary, { content: content, children: _jsx(ReactMarkdown, { components: props.unstable_componentsForTest ?? COMPONENTS, remarkPlugins: REMARK_PLUGINS, urlTransform: defaultUrlTransform, children: content }) }));
}
/**
 * Markdown renderer for ASSISTANT bubbles only — user input is not
 * markdown-authored and stays literal plain text (bubbles.tsx).
 */
export const MarkdownContent = memo(MarkdownContentImpl);
//# sourceMappingURL=markdown-content.js.map